import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {computed, observable} from 'mobx';
import {CSSProperties} from 'react';
import { ConfigUtil } from '@mintdata-wsp/common/util/ConfigUtil';

export const LEARN_EXPERIENCE_MESSAGE:string = 'lx_message';

export enum LearnExperienceFunction
{
    SET_POSITION = 'lx_set_position',
    SET_SIZE = 'lx_set_size',
    GET_FRAME_BOUNDS = 'lx_get_frame_bounds',
    SET_FRAME_BOUNDS = 'lx_set_frame_bounds',
    COPY_PAGE_FROM = 'lx_copy_page_from',
    GET_ACTIVE_PAGE = 'lx_get_active_page',
    SET_ACTIVE_PAGE = 'lx_set_active_page',
    GET_ACTIVE_SHEET = 'lx_get_active_sheet',
    SET_ACTIVE_SHEET = 'lx_set_active_sheet',
    GET_SELECTED_COMPONENT = 'lx_get_selected_component',
    SET_SELECTED_COMPONENT = 'lx_set_selected_component',
    GET_SELECTED_CELL = 'lx_get_selected_cell',
    SET_SELECTED_CELL = 'lx_set_selected_cell',
    CLOSE = 'lx_close',
    GET_CURRENT_CONTEXT = 'lx_get_current_context',
    GET_SCREEN_BOUNDS = 'lx_get_screen_bounds',
    GET_UI_PART_STATE = 'lx_get_ui_part_state',
    SET_UI_PART_STATE = 'lx_set_ui_part_state',
    ADD_COMPONENT = 'lx_add_component',
    REMOVE_COMPONENT = 'lx_remove_component',
    SET_COMPONENT_POSITION = 'lx_set_component_position',
    MOVE_COMPONENT = 'lx_move_component',
    SET_BINDING = 'lx_set_binding',
    SET_FRAME_POSITION_RELATIVE = 'lx_set_frame_position_relative',
    LX_GET_CELL_STATE = 'lx_get_cell_state',
    LX_SET_CELL_STATE = 'lx_set_cell_state',
    LX_GET_FRAME_DOCK = 'lx_get_frame_dock',
    LX_SET_FRAME_DOCK = 'lx_set_frame_dock',
    LX_GET_FRAME_FULL_OVERLAY = 'lx_get_frame_full_overlay',
    LX_SET_FRAME_FULL_OVERLAY = 'lx_set_frame_full_overlay'
}

export type LXHorizontalAlignment = 'left' | 'center' | 'right' | number | string;
export type LXVerticalAlignment = 'top' | 'middle' | 'bottom' | number | string;

export interface ILearnExperienceData
{
    readonly lxFunction:LearnExperienceFunction;
}

export interface ILearnExperienceSetPositionData extends ILearnExperienceData
{
    readonly horizontalAlign:LXHorizontalAlignment;
    readonly verticalAlign:LXVerticalAlignment;
}

export interface ILearnExperienceSetSizeData extends ILearnExperienceData
{
    readonly width:string | number;
    readonly height:string | number;
}

export interface ILearnExperienceGetBoundsData extends ILearnExperienceData
{
    readonly x:number;
    readonly y:number;
    readonly width:number;
    readonly height:number;
}

export interface ILearnExperienceSetFrameBoundsData extends ILearnExperienceData
{
    readonly horizontalAlign:LXHorizontalAlignment;
    readonly verticalAlign:LXVerticalAlignment;
    readonly width:string | number;
    readonly height:string | number;
}

export interface ILearnExperienceCopyPageFromData extends ILearnExperienceData
{
    readonly projectId:number;
    readonly designId:number;
    readonly pageNames:Array<string>;
    readonly isImportSheets:boolean;
    readonly importPrefix:string;
}

export interface ILearnExperienceActivePageData extends ILearnExperienceData
{
    readonly pageName:string;
}

export interface ILearnExperienceActiveSheetData extends ILearnExperienceData
{
    readonly sheetName:string;
}

export interface ILearnExperienceComponentData extends ILearnExperienceData
{
    readonly pageName:string;
    readonly componentName:string;
}

export interface ILearnExperienceCellLocation
{
    readonly rowIndex:number;
    readonly columnIndex:number;
}

export interface ILearnExperienceCellRange
{
    readonly from:ILearnExperienceCellLocation;
    readonly to:ILearnExperienceCellLocation;
}

export interface ILearnExperienceSelectedCellData extends ILearnExperienceData
{
    readonly sheetName:string;
    readonly cellRange:ILearnExperienceCellRange;
}

export interface ILearnExperienceCurrentContextData extends ILearnExperienceData
{
    readonly organizationId:string;
    readonly userId:string;
}

export enum LearnExperienceUiPart
{
    Outline = 'outline',
    Property = 'property',
    Spreadsheet = 'spreadsheet',
    Stylebar = 'stylebar',
    Toolbar = 'toolbar'
}

export interface ILearnExperienceUiPartStateData extends ILearnExperienceData
{
    readonly part:LearnExperienceUiPart;
    readonly partState:string;
    readonly partData:string;
}

export interface ILearnExperienceAddComponentData extends ILearnExperienceComponentData
{
    readonly parentName:string;
    readonly componentType:string;
    readonly x:number;
    readonly y:number;
    readonly width:number;
    readonly height:number;
}

export interface ILearnExperienceSetComponentPositionData extends ILearnExperienceComponentData
{
    readonly x:number;
    readonly y:number;
}

export interface ILearnExperienceMoveComponentData extends ILearnExperienceComponentData
{
    readonly deltaX:number;
    readonly deltaY:number;
}

export enum LearnExperienceFramePosition
{
    BelowCentered = 'below-centered',
    BelowLeftEdgeAligned = 'below-left-edge-aligned',
    BelowRightEdgeAligned = 'below-right-edge-aligned',
    AboveCentered = 'above-centered',
    AboveLeftEdgeAligned = 'above-left-edge-aligned',
    AboveRightEdgeAligned = 'above-right-edge-aligned'
}

export enum LearnExperienceRelativeTo
{
    ActivationDot = 'activation-dot',
    SelectedComponent = 'selected-component',
    ActiveCell = 'active-cell',
    SelectedPage = 'selected-page',
    ActiveSheet = 'active-sheet',
    PropertyPanel = 'property-panel',
    RepeaterPanel = 'repeater-panel',
    EventPanel = 'event-panel'
}

export interface ILearnExperienceFramePositionRelativeData extends ILearnExperienceData
{
    readonly framePosition:LearnExperienceFramePosition;
    readonly relativeTo:LearnExperienceRelativeTo;
    readonly relativeToName:string;
}

export enum LearnExperienceCellState
{
    NotActive = 'not-active',
    Active = 'active',
    ActiveEditing = 'active.editing',
    ActiveEditingAutocomplete = 'active.editing.autocomplete'
}

export interface ILearnExperienceCellStateData extends ILearnExperienceData
{
    readonly sheetName:string;
    readonly cellRef:ILearnExperienceCellLocation;
    readonly cellState:LearnExperienceCellState;
    readonly isLazy:boolean;
    readonly isAccumulating:boolean;
    readonly isRealtime:boolean;
    readonly cellFormula:string;
    readonly cellValue:string;
}

export enum LearnExperienceFrameDockSide
{
    Left = 'left',
    Top = 'top',
    Right = 'right',
    Bottom = 'bottom',
    Floating = 'floating'
}

export interface ILearnExperienceFrameDockData extends ILearnExperienceData
{
    readonly dockSide:LearnExperienceFrameDockSide;
    readonly dockSize:string | number;
}

export interface ILearnExperienceFrameFullOverlayData extends ILearnExperienceData
{
    readonly isOverlay:boolean;
}

export interface ILearnExperienceMessage
{
    readonly id:GUID;
    readonly type:string;
    readonly data:ILearnExperienceData;
}

export class LearnExperienceState
{
    public iframeEl:HTMLIFrameElement = null;

    @observable
    public isActive:boolean = false;

    @observable
    public isDevMode:boolean = false;

    @observable
    public isLxDevMode:boolean = false;

    @observable
    public designName:string = 'learn-panel-pioneer';

    @observable
    public isVisible:boolean = false;

    @observable
    public horizontalAlign:LXHorizontalAlignment = 'center';

    @observable
    public verticalAlign:LXHorizontalAlignment = 60;

    @observable
    public width:number | string = 300;

    @observable
    public height:number | string = 400;

    @observable
    public dockSide:LearnExperienceFrameDockSide = LearnExperienceFrameDockSide.Right;

    @observable
    public dockSize:number | string = 350;

    @observable
    public isOverlay:boolean = false;

    @observable
    public zIndex:number = null;

    @computed
    public get learnAppUrl():string
    {
        if( this.isDevMode )
            return this.designName
                ? `${window.location.protocol}//${window.location.host}/${this.designName}#learn-dev=1`
                : null;
        else
        {
            const host:string = ConfigUtil.publishedAppsHost();
            return this.designName ? `${window.location.protocol}//${this.designName}.${host}` : null;
        }
    }

    @computed
    public get bounds():CSSProperties
    {
        if( this.dockSide === LearnExperienceFrameDockSide.Floating )
        {
            let left:string;
            let top:string;

            if( this.horizontalAlign === 'left' )
                left = '0px';
            else if( this.horizontalAlign === 'center' )
                left = `calc(50vw - ${Number.isInteger(this.width as number) ? this.width + 'px' : this.width} / 2)`;
            else if( this.horizontalAlign === 'right' )
                left = `calc(100vw - 100%)`;
            else if( this.horizontalAlign < 0 )
                left = `calc(100vw - 100% - ${Math.abs(this.horizontalAlign as number)}px)`;
            else
                left = `${this.horizontalAlign}px`;

            if( this.verticalAlign === 'top' )
                top = '0px';
            else if( this.verticalAlign === 'middle' )
                top = `calc(50vh - ${Number.isInteger(this.height as number) ? this.height + 'px' : this.height} / 2)`;
            else if( this.verticalAlign === 'bottom' )
                top = `calc(100vh - 100%)`;
            else if( this.verticalAlign < 0 )
                top = `calc(100vh - 100% - ${Math.abs(this.verticalAlign as number)}px)`;
            else
                top = `${this.verticalAlign}px`;

            return {
                transform: `translate3d(${left}, ${top}, 0)`,
                width: this.width,
                height: this.height
            };
        }
        else if( this.dockSide === LearnExperienceFrameDockSide.Left
            || this.dockSide === LearnExperienceFrameDockSide.Right )
        {
            return {
                width: this.dockSize
            };
        }
        else
        {
            return {
                height: this.dockSize
            };
        }
    }
}
